import React, { useEffect, useState } from 'react'
import { useCanvasSelection } from '@sketch/canvas'
import {
  useInspectorContext,
  useInspectorSelectedElement,
  useInspectorHoveredElement,
} from '../../../shares/FrameView/hooks/inspector'
import { LayersTreeItem } from './LayersTreeItem'
import * as S from './LayersTree.styles'
import {
  useAutoExpandTreeOnSelection,
  useAutoScrollToSelection,
  useCollapsedState,
  useFilteredTree,
  useVirtualizedFlattenedTree,
  useLayerTreeContextMenu,
  useRestoreScrollPosition,
} from './layersTree.hooks'
import { SketchElement, useGoToLayer } from '@sketch/inspector'
import { useLoadAllFragments } from '@sketch-hq/sketch-web-renderer'
import { LayerTreeContextMenu } from './LayerTreeContextMenu'
import { useLayerTreePersistentState } from '../LayersTreePersistentState'
import { ClearButton } from '@sketch/components'

export function LayersTree() {
  const [searchQuery, setSearchQuery] = useState('')

  const { sketchSceneRootElement, setHoveredElement } = useInspectorContext()
  const selectedElement = useInspectorSelectedElement()
  const { selectElement: setSelectedElement } = useCanvasSelection()

  const hoveredElement = useInspectorHoveredElement()
  const {
    getScrollOffset,
    setScrollOffset,
    getCollapsedElements,
    setCollapsedElements,
  } = useLayerTreePersistentState()

  const { collapsedElements, toggleCollapse, expandAllAncestors } =
    useCollapsedState(getCollapsedElements(), setCollapsedElements)

  const {
    contextMenuPosition,
    contextMenuTargetElement,
    handleContextMenu,
    handleCloseContextMenu,
  } = useLayerTreeContextMenu()

  const topLevelElementsFiltered = useFilteredTree(
    sketchSceneRootElement,
    searchQuery
  )

  const { virtualContainerRef, virtualizer, visibleLayersFlatList } =
    useVirtualizedFlattenedTree(topLevelElementsFiltered, collapsedElements)

  useAutoExpandTreeOnSelection(
    selectedElement,
    topLevelElementsFiltered,
    expandAllAncestors
  )

  const [savedScrollOffset] = useState(getScrollOffset)
  useRestoreScrollPosition(
    virtualizer,
    virtualContainerRef,
    selectedElement,
    visibleLayersFlatList,
    savedScrollOffset
  )

  useAutoScrollToSelection(virtualizer, selectedElement, visibleLayersFlatList)

  // Continuously save scroll offset so it persists when the tree unmounts
  useEffect(() => {
    virtualizer.scrollOffset && setScrollOffset(virtualizer.scrollOffset)
  }, [virtualizer.scrollOffset, setScrollOffset])

  useRequestAllFragments(sketchSceneRootElement)

  const goToLayer = useGoToLayer()

  if (!sketchSceneRootElement) {
    return null
  }

  return (
    <>
      <S.SearchContainer>
        <S.SearchInput
          type="search"
          placeholder="Search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          aria-label="Search layers"
        />
        <S.StyledSearchIcon />
        {searchQuery && (
          <ClearButton
            onClick={() => {
              setSearchQuery('')
            }}
          />
        )}
      </S.SearchContainer>
      <S.TreeContent ref={virtualContainerRef}>
        <S.VirtualizerWrapper
          style={{
            height: `${virtualizer.getTotalSize()}px`,
          }}
          role="tree"
        >
          {virtualizer.getVirtualItems().map(virtualItem => {
            const layer = visibleLayersFlatList[virtualItem.index]
            const isSelected =
              selectedElement?.identifier === layer.sketchElement.identifier
            const isHovered =
              hoveredElement?.identifier === layer.sketchElement.identifier

            return (
              <LayersTreeItem
                key={layer.sketchElement.identifier.toString()}
                layer={layer}
                isSelected={isSelected}
                isHovered={isHovered}
                onSelect={setSelectedElement}
                onHover={setHoveredElement}
                onToggleCollapse={toggleCollapse}
                goToLayer={goToLayer}
                virtualItem={virtualItem}
                onContextMenu={handleContextMenu}
              />
            )
          })}
        </S.VirtualizerWrapper>
      </S.TreeContent>

      <LayerTreeContextMenu
        position={contextMenuPosition}
        targetElement={contextMenuTargetElement}
        onClose={handleCloseContextMenu}
        onGoToLayer={goToLayer}
        onSelectLayer={setSelectedElement}
      />
    </>
  )
}

/**
 * The web renderer canvas loads its content through potentially multiple frame fragments.
 * Fragments are fetched automatically when their frame become visible in the viewport based on the current camera position.
 * If the canvas is opened with a specific zoom level and camera position, some fragments may initially be off-screen
 * and therefore not loaded until the user pans or zooms to reveal them.
 * Because the layer trees must represent the complete document structure, we need to ensure that all fragments are loaded.
 * See https://linear.app/sketch/issue/WEB-2805/layer-list-stuck-loading-indefinitely for more details
 */
function useRequestAllFragments(sketchSceneRootElement: SketchElement | null) {
  // Make sure we only request loading fragments once
  // to avoid redundant tree operations.
  const hasRequestedRef = React.useRef(false)

  const loadAllFragmentsIfNeeded = useLoadAllFragments()

  useEffect(() => {
    if (!sketchSceneRootElement || hasRequestedRef.current === true) return

    loadAllFragmentsIfNeeded()
    hasRequestedRef.current = true
  }, [loadAllFragmentsIfNeeded, sketchSceneRootElement])
}
